import { getRelativeDates } from '@/utils/time'

export default {
  /**
   * @return {Number} 前回のデータ取得からの経過時間（分）
   */
  timeDiff ({ loading, currentTimestamp, fetchedTimestamp }) {
    if (loading) return -1
    return Math.round(Math.abs(((currentTimestamp - fetchedTimestamp) / (1000 * 60))))
  },

  /**
   * @return {String} 選択しているエリア名
   */
  areaName (state, getters, { groups }) {
    const queue = [groups.group]

    while (queue.length > 0) {
      const g = queue.pop()
      if (g.id === state.area) {
        return g.name
      }
      queue.push(...g.children)
    }
  },

  /**
   * @return {object[]} チェックしてある顧客の情報
   */
  selectedCustomers (state) {
    return state.selectedIDs.map(id => state.customers.find(c => id === c.id))
  },

  /**
   * @return {Number} チェックしてある顧客全体の総給油量
   */
  selectedOilingCapacity (_, { selectedCustomers }) {
    return selectedCustomers.reduce((t, { oilingCapacity }) => (t += oilingCapacity), 0)
  },

  /**
   * @return {Number} チェックしてある顧客数
   */
  selectedNum (state) {
    return state.selectedIDs.length
  },

  /**
   *
   * @returns {object[]} {hasSensorWarning: true}の顧客
   */
  customersWithSensorWarning (state) {
    if (!state.customers.length) return []
    return state.customers.filter(c => c.hasSensorWarning)
  },
  customersWithSensorWarningCount (state, { customersWithSensorWarning }) {
    return customersWithSensorWarning.length
  },

  isFilteredBySensorWarning (state) {
    return !!state.searchText.includes(state.specialString)
  },

  /**
   *
   * @return {Number} 補給猶予が【今日中】でチェックしていない顧客数
   */
  leftDeadlines ({ customers, selectedIDs }) {
    return customers.filter(c => c.margin.label === '今日中' && !selectedIDs.includes(c.id)).length
  },
  /**
   * patternに応じた配送予定に関する情報を配列で返す。
   *
   * ids: 配送予定の顧客IDをもつSet
   * deadline: この日の補給猶予が「本日中」になる予定数
   * appointed: この日の配達指定数（過去分は[0]に加算）
   * oilingCapacity: 顧客の持つ全タンクの(最大容量 - しきい値)の合計
   * @return {{ deadline: Number, appointed: Number, oilingCapacity: Number }[]}
   */
  deliveryForecast ({ customers, selectedIDs }) {
    const pattern = [ '今日中', '2日以内', '3日以内', '4日以内', '5日以内', '6日以内' ]
    const dates = getRelativeDates(6, 0) // ['11/20', '11/21', ...]
    const res = Array.from(Array(pattern.length), () => ({ ids: new Set(), deadline: 0, appointed: 0, oilingCapacity: 0 }))

    customers.forEach(({ id, margin, tanks, deliveryDate }) => {
      const i = pattern.indexOf(margin.label)
      if (i >= 0) {
        res[i].deadline++

        if (!selectedIDs.includes(id)) {
          res[i].ids.add(id)
          res[i].oilingCapacity += getOilingCapacity(tanks)
        }
      }
      if (!deliveryDate.date) return res

      if (deliveryDate.status <= 0) { // 今日以前の指定は今日に加算
        res[0].appointed++
        if (!selectedIDs.includes(id)) {
          res[i].ids.add(id)
        }
      } else { // 明日以降の指定
        const i = dates.indexOf(deliveryDate.date)
        if (i >= 0) {
          res[i].appointed++
          if (!selectedIDs.includes(id)) {
            res[i].ids.add(id)
          }
        }
      }
    })
    return res
  },

  /* getters used by speechBubble */

  /**
   * @return {object[]} 「配送指定：将来」以外の顧客
   */
  allCustomersExceptFuture ({ customers }) {
    return customers.filter(allExceptFuture)
  },

  /**
   * @return {Number} 「配送指定：将来」以外の顧客数
   */
  caseNumberMax (_, { allCustomersExceptFuture }) {
    return allCustomersExceptFuture.length
  },

  /**
   * @return {String[]} 顧客の補給猶予を配列で返す。
   */
  marginArray ({ customers }) {
    return customers.filter(noDeliveryDate).map(e => e.margin.label)
  },

  /**
   * @return {String[]} 補給猶予のうち重複を削除して配列で返す。
   */
  margins (_, { marginArray }) {
    return marginArray.filter((v, i) => marginArray.indexOf(v) === i)
  },

  /**
   * @return {Number} 今日に配送日指定されている顧客数
   */
  customersArrayTopOffset ({ customers }) {
    return customers.filter(todayAndPast).length
  },
  /**
   * @return {{ i: Number, benchMark: Number }[]} 「給油量」の吹き出しの各ポジションとベンチマークを配列で返す。
   */
  oilSupplySpeechBubbleIndexes ({ oilSupplyInput }, { allCustomersExceptFuture }) {
    let capacity = 0
    let benchmarkMultiplier = 2
    let benchMark = Math.max(oilSupplyInput, 1000)
    const tempArr = []

    for (let i = 0, len = allCustomersExceptFuture.length; i < len; i++) {
      capacity += allCustomersExceptFuture[i].oilingCapacity
      if (capacity > benchMark) {
        tempArr.push({
          i: Math.max(i - 1, 0),
          benchMark
        })
        benchMark = oilSupplyInput * benchmarkMultiplier
        ++benchmarkMultiplier
        capacity -= allCustomersExceptFuture[i].oilingCapacity
        --i
      } else if (capacity === benchMark) {
        tempArr.push({ i, benchMark })
        benchMark = oilSupplyInput * benchmarkMultiplier
        ++benchmarkMultiplier
      }
    }
    return tempArr
  },
  /**
   * @return {{ i: Number, benchMark: Number, color: String }[]} 「補給猶予」の吹き出しの各ポジションとベンチマーク、色を配列で返す。
   */
  oilMarginIndexes ({ customers }, { customersArrayTopOffset, marginArray, margins }) {
    let result = margins.map(e => {
      let benchMarkIndex = marginArray.lastIndexOf(e) + customersArrayTopOffset
      return {
        i: benchMarkIndex,
        benchMark: e,
        color: customers[benchMarkIndex].margin.style['background-color']
      }
    })
    return result
  },
  speechBubbleContent ({ customers, caseNumberInput }, getters) {
    let result = []

    customers.forEach((e, i) => {
      let temp = []
      let oilSupplySpeechBubbleItem = getters.oilSupplySpeechBubbleIndexes.filter(f => f.i === i)
      let oilMarginSpeechBubbleItem = getters.oilMarginIndexes.find(f => f.i === i)

      if (i === caseNumberInput - 1) {
        temp.push({
          benchMark: `${caseNumberInput} 件`,
          color: '#50C878'
        })
      }
      if (oilSupplySpeechBubbleItem.length) {
        oilSupplySpeechBubbleItem.forEach(f => {
          temp.push({
            benchMark: `${f.benchMark} ℓ`,
            color: '#795548'
          })
        })
      }
      if (oilMarginSpeechBubbleItem) {
        temp.push({
          benchMark: oilMarginSpeechBubbleItem.benchMark,
          color: oilMarginSpeechBubbleItem.color
        })
      }

      result.push(temp)
    })

    return result
  }
}

/**
 * タンクの配列を渡すと総給油可能量（タンクの空きの合計）を返す。
 * @param {{ oilMaxVolume: Number }[]} arr
 * @return {Number} タンクの空きの合計。
 */
const getOilingCapacity = arr => arr.reduce((acc, el) => (acc += (el.legalMax - el.volumeThreshold)), 0)

// functions used by speechBubble getters

const noDeliveryDate = d => !d.deliveryDate.date
const todayAndPast = d => d.deliveryDate.status <= 0
const allExceptFuture = d => (!d.deliveryDate.date || d.deliveryDate.status <= 0)
